import moment from "moment";
import { Booking } from "../../calendar/booking/types";
import {
  AvailabilityData,
  BookingHorizon,
  CustomEntry,
  DayHours,
} from "../../calendar/setAvailability/types";
import { WEEKDAY_INDEX_TO_KEY } from "../../calendar/setAvailability/constants";

// Mirrors SAME_DAY_NOTICE_MINUTES in ev-server/src/booking/slots.ts.
export const SAME_DAY_NOTICE_MINUTES = 120;

/** The first date a customer can book: today only with same-day bookings on. */
export const earliestBookableDate = (
  availability: Pick<AvailabilityData, "allowSameDayBookings"> | null,
): string =>
  (availability?.allowSameDayBookings
    ? moment()
    : moment().add(1, "day")
  ).format("YYYY-MM-DD");

export interface Slot {
  from: string; // "HH:mm"
  to: string; // "HH:mm"
}

const toMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const toTime = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
    minutes % 60,
  ).padStart(2, "0")}`;

// Does a custom-date entry cover the given ISO date?
const entryCoversDate = (entry: CustomEntry, date: string): boolean => {
  if (entry.mode === "single") {
    return entry.date === date;
  }
  return date >= entry.startDate && date <= entry.endDate;
};

// Resolve the effective open window for a date (weekly hours overridden by any
// matching custom date). Returns null when the vendor is closed that day.
const effectiveWindow = (
  date: string,
  availability: AvailabilityData,
): DayHours | null => {
  const dayKey = WEEKDAY_INDEX_TO_KEY[moment(date, "YYYY-MM-DD").day()];
  let window: DayHours | null = availability.weekly[dayKey].available
    ? availability.weekly[dayKey]
    : null;

  // Last matching custom entry wins (most-recently added override).
  const override = [...availability.customDates]
    .reverse()
    .find((e) => entryCoversDate(e, date));

  if (override) {
    if (override.mode === "single") {
      window =
        override.availability === "unavailable"
          ? null
          : {
              available: true,
              from: override.from ?? "09:00",
              to: override.to ?? "17:00",
            };
    } else if (override.availability === "unavailable") {
      window = null;
    } else if (override.availability === "sameHours") {
      window = {
        available: true,
        from: override.from ?? "09:00",
        to: override.to ?? "17:00",
      };
    } else {
      // perDay
      const day = override.perDay?.find((p) => p.date === date);
      window =
        day && day.available
          ? { available: true, from: day.from, to: day.to }
          : null;
    }
  }

  return window;
};

const HORIZON_DAYS: Record<Exclude<BookingHorizon, "unlimited">, number> = {
  "2w": 14,
  "4w": 28,
  "2m": 60,
  "3m": 90,
};

/**
 * Latest selectable date (ISO) given the booking horizon, or undefined when
 * there is no limit.
 *
 * An unrecognised horizon counts as no limit, not as zero days: values stored
 * before these codes existed would otherwise resolve to today and silently
 * collapse the calendar to a single selectable day.
 */
export const horizonMaxDate = (horizon: BookingHorizon): string | undefined => {
  const days = HORIZON_DAYS[horizon as Exclude<BookingHorizon, "unlimited">];

  return days == null
    ? undefined
    : moment().add(days, "days").format("YYYY-MM-DD");
};

/** The earlier of two ISO dates; either side may be absent. */
export const earlierDate = (
  a: string | undefined,
  b: string | undefined,
): string | undefined => (a && b ? (a < b ? a : b) : (a ?? b));

interface SlotOptions {
  slotStepMinutes?: number; // default 30
  excludeBookingId?: string; // the meeting being rescheduled
}

/**
 * Discrete available start slots for a date, given the vendor's availability and
 * existing bookings (overlaps within `bufferMinutes` are removed). Past slots on
 * the current day are excluded.
 */
export const availableSlots = (
  date: string,
  durationMinutes: number,
  availability: AvailabilityData,
  bookings: Booking[],
  options: SlotOptions = {},
): Slot[] => {
  const window = effectiveWindow(date, availability);
  if (!window) {
    return [];
  }

  // An all-day booking is how a vendor blocks the day off. It has no from/to
  // times, so it can never conflict in the interval check below — the day has
  // to be closed outright. Mirrors validateRequestedSlot in ev-server.
  const blockedAllDay = bookings.some(
    (b) => b.date === date && b.allDay && b.id !== options.excludeBookingId,
  );
  if (blockedAllDay) {
    return [];
  }

  const step = options.slotStepMinutes ?? 30;
  const buffer = availability.bufferMinutes;
  const open = toMinutes(window.from);
  const close = toMinutes(window.to);
  const duration = Math.max(durationMinutes, step);

  // Busy intervals (with buffer) from existing same-day, non-all-day bookings.
  const busy = bookings
    .filter(
      (b) => b.date === date && !b.allDay && b.id !== options.excludeBookingId,
    )
    .map((b) => ({
      start: toMinutes(b.from) - buffer,
      end: toMinutes(b.to) + buffer,
    }));

  const isToday = date === moment().format("YYYY-MM-DD");
  if (isToday && !availability.allowSameDayBookings) {
    return [];
  }
  // Same-day slots need notice, so "now" is pushed forward by it.
  const nowMinutes =
    moment().hours() * 60 + moment().minutes() + SAME_DAY_NOTICE_MINUTES;

  const slots: Slot[] = [];
  for (let start = open; start + duration <= close; start += step) {
    const end = start + duration;
    if (isToday && start < nowMinutes) {
      continue;
    }
    const conflicts = busy.some((b) => start < b.end && end > b.start);
    if (!conflicts) {
      slots.push({ from: toTime(start), to: toTime(end) });
    }
  }
  return slots;
};
